/**
 * Phase 1.7 — dashboard KPI strip.
 *
 * Pure presentation. Caller passes `data` (DashboardSummary) and we render
 * five KpiCards in the spec-mandated split-metric layout.
 */

import { Bug, Truck, Package, Calendar, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { KpiCard } from '@/components/v2';
import type { DashboardSummary } from '@shared/types';

export interface KpiStripProps {
  summary: DashboardSummary;
}

export function KpiStrip({ summary }: KpiStripProps): JSX.Element {
  const navigate = useNavigate();
  // Animals: active vs sold/deceased — the summary returns `animalCount`
  // alone today. Until the API splits status counts (Phase 1.10 work),
  // we surface deceased/sold as 0 placeholders. The card will show 0
  // and not break the layout; the comment here is the breadcrumb for
  // the future API change.
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <KpiCard
        label="Animals"
        icon={Bug}
        primary={{ label: 'Active', value: summary.animalCount }}
        secondary={{ label: 'Sold/Deceased', value: 0, tone: 'warning' }}
        onClick={() => navigate('/animals')}
      />
      <KpiCard
        label="Vehicles"
        icon={Truck}
        primary={{ label: 'In Service', value: summary.vehicleCount }}
        secondary={{ label: 'Maintenance', value: 0, tone: 'warning' }}
        onClick={() => navigate('/vehicles')}
      />
      <KpiCard
        label="Inventory"
        icon={Package}
        primary={{ label: 'In Stock', value: summary.inventoryCount }}
        secondary={{
          label: 'Below Threshold',
          value: summary.lowStockCount,
          tone: summary.lowStockCount > 0 ? 'warning' : 'default',
        }}
        onClick={() => navigate('/inventory')}
      />
      <KpiCard
        label="Tasks"
        icon={Calendar}
        primary={{
          label: 'Overdue',
          value: summary.overdueTasks.count,
          tone: summary.overdueTasks.count > 0 ? 'warning' : 'positive',
        }}
        secondary={{ label: 'Open', value: summary.openTaskCount }}
      />
      <KpiCard
        label="Documents"
        icon={FileText}
        primary={{
          label: 'Processed',
          value: summary.documentCount - summary.pendingDocCount,
          tone: 'positive',
        }}
        secondary={{
          label: 'Needs Review',
          value: summary.pendingDocCount,
          tone: summary.pendingDocCount > 0 ? 'warning' : 'default',
        }}
        onClick={() => navigate('/documents')}
      />
    </div>
  );
}
